import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { LuLoaderCircle, LuCircleAlert } from 'react-icons/lu';
import AuthShell from '../components/AuthShell';

/**
 * /confirmar-email?token=… — o destino do link que vai no e-mail.
 *
 * É anônima de propósito: a pessoa pode abrir o e-mail em outro navegador, ou
 * no celular, onde não existe sessão. Quem já está logado volta para a Home
 * (sem a faixa); quem não está cai no login com o aviso de confirmado.
 */
const ConfirmarEmailPage = () => {
  const apiUrl = import.meta.env.VITE_API_URL;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [erro, setErro] = useState<string | null>(null);

  /* O StrictMode monta duas vezes em desenvolvimento e o token serve UMA vez —
     sem esta trava, a segunda chamada recebe "link não é mais válido" e a tela
     mostra erro depois de ter dado certo. */
  const jaTentou = useRef(false);

  useEffect(() => {
    if (jaTentou.current) return;
    jaTentou.current = true;

    const token = searchParams.get('token');
    if (!token) {
      setErro('Link de confirmação inválido.');
      return;
    }

    (async () => {
      try {
        const resposta = await fetch(`${apiUrl}/api/Auth/ConfirmarEmail`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        const dados = await resposta.json().catch(() => ({}));

        if (!resposta.ok || dados.sucesso === false) {
          setErro(dados.mensagem || dados.message || 'Este link de confirmação não é mais válido.');
          return;
        }

        /* Logado: o Me traz emailConfirmado = true e a faixa da Home some sozinha. */
        const logado = !!localStorage.getItem('user');
        if (logado) {
          const me = await fetch(`${apiUrl}/api/Auth/Me`, { credentials: 'include' });
          if (me.ok) localStorage.setItem('user', JSON.stringify(await me.json()));
          navigate('/home', { replace: true });
        } else {
          navigate('/login?confirmado=1', { replace: true });
        }
      } catch {
        setErro('Não conseguimos falar com o servidor. Tente abrir o link de novo.');
      }
    })();
  }, [apiUrl, navigate, searchParams]);

  if (erro) {
    return (
      <AuthShell>
        <h1 className="au-h1">Link não confirmado</h1>
        <div className="au-alert is-danger" role="alert">
          <LuCircleAlert size={16} />
          <span>{erro}</span>
        </div>
        <p className="au-help" style={{ marginTop: 14 }}>
          Entre no sistema e use o <strong>Reenviar</strong> na faixa da tela inicial para receber um
          link novo.
        </p>
        <Link to="/login" className="au-btn" style={{ textDecoration: 'none' }}>
          Ir para o login
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="au-h1">Confirmando o seu e-mail…</h1>
      <p className="au-sub">
        <LuLoaderCircle size={15} className="au-spin" style={{ verticalAlign: '-2px' }} /> Só um
        instante.
      </p>
    </AuthShell>
  );
};

export default ConfirmarEmailPage;
